import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Emergent Google OAuth callback screen.
 *
 * Displayed synchronously (from AppRouter render) whenever
 * `window.location.hash` contains `session_id=`. It POSTs the session id to
 * our backend, receives our own `md_customer_session` JWT cookie, then
 * navigates to /compte with the URL fragment stripped.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
const AuthCallback = () => {
  const { completeGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Strip the fragment immediately so a refresh doesn't retry
    if (window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    if (!sessionId) {
      setError("Session Google introuvable. Merci de réessayer.");
      return;
    }

    (async () => {
      try {
        await completeGoogleLogin(sessionId);
        navigate("/compte", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Connexion Google impossible pour le moment.");
      }
    })();
  }, [completeGoogleLogin, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="auth-callback">
      <div className="max-w-md w-full text-center border border-[color:var(--gold)]/30 p-10 bg-[#0A0A0A]">
        {error ? (
          <>
            <p className="label-caps text-red-400 text-[10px] tracking-[0.3em] mb-3">Erreur d'authentification</p>
            <p className="font-serif text-2xl text-white mb-6">Connexion interrompue</p>
            <p className="text-white/60 text-sm mb-8">{error}</p>
            <button
              onClick={() => navigate("/connexion", { replace: true })}
              className="bg-[color:var(--gold)] text-black hover:bg-white px-6 py-2 label-caps text-[10px] tracking-[0.25em]"
              data-testid="auth-callback-retry"
            >
              Retour à la connexion
            </button>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border border-[color:var(--gold)]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 size={20} className="text-[color:var(--gold)] animate-spin" />
            </div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-3">Signature en cours</p>
            <p className="font-serif text-2xl text-white">Finalisation de votre connexion…</p>
            <p className="text-white/40 text-xs mt-4">Nous ouvrons votre compte MAISON DOUBA en toute sécurité.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
